import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../shared/contexts/AuthContext';
import HighLogo from '../HighLogo';
import logo from "../../assets/logo.png";
import NotificationSign from '../components/NotificationSign';
import { useTranslation } from 'react-i18next';
import { Bell } from 'lucide-react';


const GOLD = '#D4AF37';
const TEXT_DEFAULT = '#ccc';
const BG = '#111';
const BORDER_DEFAULT = '#222';

const SidebarMyProfile: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { logout } = useAuth();
  const { t } = useTranslation();

  const isActive = (match: string | string[]) => {
    const arr = Array.isArray(match) ? match : [match];
    return arr.some((p) =>
      p === '/' ? location.pathname === '/' : location.pathname.startsWith(p)
    );
  };

  const Button: React.FC<{
    to?: string;
    onClick?: () => void;
    activeMatch?: string | string[];
    children: React.ReactNode;
  }> = ({ to, onClick, activeMatch, children }) => {
    const [hovered, setHovered] = useState(false);
    const active = activeMatch ? isActive(activeMatch) : !!(to && isActive(to));
    const style: React.CSSProperties = {
      display: 'block',
      width: '100%',
      padding: '8px 10px',
      marginBottom: '8px',
      backgroundColor: BG, // ✅ always dark
      color: TEXT_DEFAULT, // ✅ always light
      border: `1px solid ${active ? GOLD : hovered ? GOLD : BORDER_DEFAULT}`, // ✅ active = permanent gold border
      borderRadius: 8,
      cursor: 'pointer',
      fontWeight: 500,
      textAlign: 'left',
      fontSize: 14,
      transition: 'border-color 120ms ease',
      // optional: makes active feel a bit more "locked in" without filling:
      // boxShadow: active ? `0 0 0 1px ${GOLD}` : 'none',
    };     
    
    return (
      <button
        style={style}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        onFocus={() => setHovered(true)}
        onBlur={() => setHovered(false)}
        onClick={() => (to ? navigate(to) : onClick?.())}
      >
        {children}
      </button>
    );
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };
  return (
    <aside style={overlayContainer}>
      {/* Top bar inside Sidebar */}
  <div
  style={{
    position: "relative",
    display: "flex",
    alignItems: "center",
    padding: "12px 20px",
    marginBottom: "-20px",
    marginTop: "1px",
    color: "#fff",
    minHeight: 64,
  }}
>
  <div
    style={{
      marginLeft: "-25%",
      marginTop: "9%",
      marginBottom: "13%",
    }}
  >
  <HighLogo
  src={logo}
  style={{ width: "70%" }}
  padding={0}
  onClick={() => navigate("/gallery")}
 />
  </div>

  <div
    style={{
      position: "absolute",
      top: 34,
      right: 30,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      zIndex: 10,
    }}
  >
    <NotificationSign
      to="/notifications"
      icon={<Bell size={21} color="#ff8c00" strokeWidth={2.2} />}
    />
  </div>
</div>
      <hr style={hr} />

      <div style={sectionTitle}>{t("sidebarMyProfile.sections.profile")}</div>
            
      {/* Menu */}
      <nav style={{ marginTop: "10px" }}>
        <Button to="/profile" activeMatch="/profile">
          👻 {t("sidebarMyProfile.buttons.profile")}
        </Button>
        <Button to="/my-posts" activeMatch="/my-posts">
          📭 {t("sidebarMyProfile.buttons.postings")}
        </Button>
        <Button to="/followers-list" activeMatch="/followers-list">
          📚 {t("sidebarVisitor.buttons.followers")}
        </Button>
        <Button to="/following-list" activeMatch="/following-list">
          📚 {t("sidebarVisitor.buttons.follow")}
        </Button>
        
  
        <hr style={{ margin: "16px 0", borderColor: "#333" }} />
        <div style={sectionTitle}>{t("sidebarMyProfile.sections.noti")}</div>

        <Button to="/notifications" activeMatch="/notifications">
          🔔 {t("sidebarMyProfile.buttons.notifications")}
        </Button>
  
        <hr style={{ margin: "16px 0", borderColor: "#333" }} />
        <div style={sectionTitle}>{t("sidebarMyProfile.sections.chat")}</div>

        <Button onClick={() => navigate("/chat")}>
          💬 {t("sidebarMyProfile.buttons.chat")}
        </Button>
        
        <hr style={{ margin: "16px 0", borderColor: "#333" }} />
        <div style={sectionTitle}>{t("sidebarMyProfile.sections.nav")}</div>

        <Button onClick={() => navigate("/gallery")}>
          🖼 {t("sidebarMyProfile.buttons.gallery")}
        </Button>
        <Button onClick={() => navigate("/following")}>
          🌊 {t("sidebarMyProfile.buttons.myPreferences")}
        </Button>
        <Button onClick={() => navigate("/mylikes")}>
          ❤️ {t("sidebarMyProfile.buttons.myLikes")}
        </Button>
  
        
  
        <hr style={{ margin: "16px 0", borderColor: "#333" }} />
  
        <Button to="/settings/overview" activeMatch="/settings/overview">
          ⚙️ {t("sidebarMyProfile.buttons.settings")}
        </Button>
        <Button onClick={handleLogout}>
          📕 {t("sidebarMyProfile.buttons.logout")}
        </Button>

        <div
          style={{
            marginTop: 20,
            fontSize: 10,
            color: "#555",
            lineHeight: 1.6,
            textAlign: "left",
          }}
        >
          <span
            style={{ cursor: "pointer" }}
            onClick={() => navigate("/helpsupportstart")}
          >
            {t("mainSidebar.footer.helpSupport")}
          </span>

          {" · "}

          <span
            style={{ cursor: "pointer" }}
            onClick={() => navigate("/legal/privacypolicy")}
          >
            {t("mainSidebar.footer.privacyPolicy")}
          </span>

          {" · "}

          <span
            style={{ cursor: "pointer" }}
            onClick={() => navigate("/legal/termsofuse")}
          >
            {t("mainSidebar.footer.termsOfUse")}
          </span>
        </div>
        
      </nav>
    </aside>
  );
  
};

export default SidebarMyProfile;

/* ---------------- Styles ---------------- */

const overlayContainer: React.CSSProperties = {
  width: '230px',
  background:
    'linear-gradient(145deg, rgba(5, 9, 20, 0.95), rgba(4, 7, 15, 0.95))',
  color: '#bbb',
  padding: '20px',
  height: 'calc(100vh)',
  boxShadow: '2px 0 5px rgba(0,0,0,0.1)',
  position: 'fixed',
  marginTop: '-2%',
  overflow: 'hidden',
};

const BORDER = '#222';

const hr: React.CSSProperties = {
  border: 0,
  borderTop: `1px solid ${BORDER}`,
  margin: '8px 0',
  marginBottom: 14.5,
};

const sectionTitle: React.CSSProperties = {
  color: '#bbb',
  fontWeight: 700,
  marginBottom: 10,
  fontSize: 13,
  letterSpacing: 0.3,
  textTransform: 'uppercase',
};
